/**
 * Single validated entry point for environment configuration.
 * Import `env` from here — never touch `import.meta.env` elsewhere.
 */

type AppEnvironment = 'development' | 'staging' | 'production'
type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent'

// Vite types `import.meta.env` with an `any` index signature; narrow it once.
const rawEnv = import.meta.env as unknown as Record<string, string | undefined>

function required(key: keyof ImportMetaEnv): string {
  const value = rawEnv[key]
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(
      `[env] Missing required environment variable "${key}". ` +
        `Add it to .env.<mode> or .env.local (see .env.example).`,
    )
  }
  return value
}

function optional(key: keyof ImportMetaEnv, fallback = ''): string {
  const value = rawEnv[key]
  return typeof value === 'string' && value.length > 0 ? value : fallback
}

function numeric(key: keyof ImportMetaEnv, fallback: number): number {
  const parsed = Number(optional(key))
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

const appEnv = optional('VITE_APP_ENV', 'development') as AppEnvironment

export const env = {
  appName: optional('VITE_APP_NAME', 'corvinusHUB'),
  appEnv,
  isDevelopment: appEnv === 'development',
  isStaging: appEnv === 'staging',
  isProduction: appEnv === 'production',
  logLevel: optional('VITE_LOG_LEVEL', 'info') as LogLevel,

  api: {
    baseUrl: required('VITE_API_BASE_URL'),
    timeoutMs: numeric('VITE_API_TIMEOUT_MS', 30_000),
  },

  /** Store link behind the "Download mobile app" button; empty = no link yet. */
  mobileAppUrl: optional('VITE_MOBILE_APP_URL'),

  entra: {
    clientId: optional('VITE_ENTRA_CLIENT_ID'),
    tenantId: optional('VITE_ENTRA_TENANT_ID'),
    redirectUri: optional('VITE_ENTRA_REDIRECT_URI', globalThis.location.origin),
    postLogoutRedirectUri: optional(
      'VITE_ENTRA_POST_LOGOUT_REDIRECT_URI',
      globalThis.location.origin,
    ),
    /** Scopes for our own API, requested on token acquisition. */
    apiScopes: optional('VITE_ENTRA_API_SCOPES').split(' ').filter(Boolean),
  },
} as const

/** True when Entra ID is configured; otherwise auth runs in a no-op guest mode. */
export const isAuthConfigured: boolean =
  env.entra.clientId.length > 0 && env.entra.tenantId.length > 0
